import React from "react";
import { motion } from "framer-motion";
import { Globe2, ShieldAlert, XCircle, Info } from "lucide-react";
import { Section, Reveal } from "@/components/Primitives";
import { useLang } from "@/context/LanguageContext";

const COPY = {
  fr: {
    eyebrow: "Passeports éligibles & Conditions importantes",
    passportsTitle: "Passeports éligibles",
    passportsIntro: "Les titulaires des passeports suivants peuvent être accompagnés par Work & Travel Expert :",
    passportsNote: "D'autres nationalités peuvent être étudiées au cas par cas selon les critères d'immigration en vigueur.",
    warnTitle: "Avertissements importants",
    warnIntro: "Avant de déposer une demande de visa pour le Canada ou l'Australie, veuillez vérifier les points suivants :",
    warnListTitle: "Votre demande peut être refusée notamment si :",
    reasons: [
      "Vous avez un casier judiciaire ou des antécédents criminels.",
      "Vous fournissez de faux documents ou de fausses informations.",
      "Vous ne remplissez pas les critères du visa demandé.",
      "Votre passeport est expiré ou n'est pas valide.",
      "Vous souffrez d'une maladie pouvant entraîner une inadmissibilité selon les autorités compétentes.",
      "Vous ne disposez pas des ressources financières exigées.",
      "Vous avez déjà dépassé la durée autorisée d'un précédent visa ou enfreint les lois d'immigration.",
      "Vous avez déjà fait l'objet d'un refus pour fraude ou fausse déclaration.",
      "Vous ne respectez pas les exigences des autorités d'immigration.",
    ],
    infoTitle: "Information importante",
    infoText: "La décision finale appartient exclusivement aux autorités d'immigration (IRCC pour le Canada et Department of Home Affairs pour l'Australie). Work & Travel Expert accompagne les candidats dans leurs démarches administratives afin de maximiser leurs chances de présenter un dossier complet, mais ne peut jamais garantir l'obtention d'un visa.",
    passports: [
      { flag: "🇫🇷", label: "Passeport français" },
      { flag: "🇧🇪", label: "Passeport belge" },
      { flag: "🇨🇭", label: "Passeport suisse" },
      { flag: "🇱🇺", label: "Passeport luxembourgeois" },
    ],
  },
  en: {
    eyebrow: "Eligible passports & Important conditions",
    passportsTitle: "Eligible passports",
    passportsIntro: "Holders of the following passports can be supported by Work & Travel Expert:",
    passportsNote: "Other nationalities may be considered on a case-by-case basis according to the immigration criteria in force.",
    warnTitle: "Important warnings",
    warnIntro: "Before submitting a visa application for Canada or Australia, please check the following points:",
    warnListTitle: "Your application may be refused, in particular if:",
    reasons: [
      "You have a criminal record or criminal history.",
      "You provide false documents or false information.",
      "You do not meet the requirements of the requested visa.",
      "Your passport is expired or invalid.",
      "You suffer from a condition that may lead to inadmissibility according to the competent authorities.",
      "You do not have the required financial resources.",
      "You have previously overstayed a visa or breached immigration laws.",
      "You have previously been refused for fraud or misrepresentation.",
      "You do not meet the requirements of the immigration authorities.",
    ],
    infoTitle: "Important information",
    infoText: "The final decision rests exclusively with the immigration authorities (IRCC for Canada and the Department of Home Affairs for Australia). Work & Travel Expert supports applicants in their administrative procedures to maximize their chances of submitting a complete file, but can never guarantee that a visa will be granted.",
    passports: [
      { flag: "🇫🇷", label: "French passport" },
      { flag: "🇧🇪", label: "Belgian passport" },
      { flag: "🇨🇭", label: "Swiss passport" },
      { flag: "🇱🇺", label: "Luxembourg passport" },
    ],
  },
};

export const EligibilitySection = () => {
  const { lang } = useLang();
  const c = COPY[lang];

  return (
    <section data-testid="eligibility-section" className="bg-white">
      <Section className="py-24">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold">
            📘 {c.eyebrow}
          </span>
        </Reveal>

        {/* PASSEPORTS ÉLIGIBLES */}
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl text-navy flex items-center gap-3">
            <Globe2 className="text-gold" size={30} /> {c.passportsTitle}
          </h2>
          <div className="gold-line mt-5" />
          <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">{c.passportsIntro}</p>
        </Reveal>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="eligible-passports">
          {c.passports.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 + i * 0.07 }}
              className="flex items-center gap-3.5 rounded-xl border border-border bg-[#F8F9FA] px-5 py-4 hover:border-gold transition-colors duration-300"
            >
              <span className="text-3xl leading-none shrink-0">{p.flag}</span>
              <span className="text-navy font-medium text-sm sm:text-base leading-snug">{p.label}</span>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-6 flex items-start gap-3 border-l-2 border-gold bg-[#F8F9FA] px-5 py-4 rounded-r-xl">
            <Globe2 size={18} className="text-gold shrink-0 mt-0.5" />
            <p className="text-sm text-navy">{c.passportsNote}</p>
          </div>
        </Reveal>

        {/* AVERTISSEMENTS */}
        <Reveal delay={0.05}>
          <h2 className="mt-16 font-heading text-3xl sm:text-4xl text-navy flex items-center gap-3">
            <ShieldAlert className="text-red-500" size={30} /> {c.warnTitle}
          </h2>
          <div className="gold-line mt-5" />
          <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">{c.warnIntro}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50/60 p-6 sm:p-8">
            <p className="font-medium text-navy flex items-center gap-2">
              <XCircle size={20} className="text-red-500 shrink-0" /> {c.warnListTitle}
            </p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {c.reasons.map((r, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: 0.05 + i * 0.05 }}
                  className="flex items-start gap-3 text-sm text-navy"
                  data-testid={`refusal-reason-${i}`}
                >
                  <XCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                  <span>{r}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* INFORMATION IMPORTANTE */}
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-2xl bg-navy text-white p-6 sm:p-8 flex items-start gap-4" data-testid="eligibility-info">
            <div className="h-11 w-11 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold shrink-0">
              <Info size={20} />
            </div>
            <div>
              <p className="font-heading text-xl text-white">{c.infoTitle}</p>
              <p className="mt-2 text-white/80 text-sm sm:text-base leading-relaxed">{c.infoText}</p>
            </div>
          </div>
        </Reveal>
      </Section>
    </section>
  );
};
